import { gql, useQuery } from "@apollo/client";
import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { AppErrorText, AppText, Loading } from "src/Components/UI";
import { AppTable, TableRow, TableTD } from "src/Components/UI/AppTable";
import { LeadCardContext } from "src/context/LeadCardContext";
import { SYSTEM_FIELD_NAME_TO_LABEL } from "src/utils/misc";
import styled from "styled-components";

const FETCH_LEAD_HISTORY_CHANGES = gql`
  query fetchLeadHistoryChanges($lead_id: String!, $skip: Int, $take: Int) {
    fetchLeadHistoryChanges(lead_id: $lead_id, skip: $skip, take: $take) {
      id
      lead_id
      created_at
      custom_updated_by
      user {
        id
        full_name
      }
      field
      old_value
      new_value
    }
  }
`;

export const ChangeHistoryTab: React.FC<{ leadId: string }> = ({ leadId }) => {
  const [refreshLeadHistoryChanges, setRefreshLeadHistoryChanges] = useState(false);

  const { leadHistoryChangedList, setLeadHistoryChangedList } = useContext(LeadCardContext);

  // Used to refresh the table when
  useEffect(() => {
    const thisLeadUpdated = leadHistoryChangedList.includes(leadId);

    if (thisLeadUpdated) {
      setRefreshLeadHistoryChanges(true);
      // use a timeout for 500 ms and flip the state back to false
      setTimeout(() => {
        setRefreshLeadHistoryChanges(false);
        setLeadHistoryChangedList(leadHistoryChangedList.filter((id) => id !== leadId));
      }, 100);
    }
  }, [leadHistoryChangedList, leadId]);

  return refreshLeadHistoryChanges ? <Loading /> : <ChangeHistoryTable leadId={leadId} />;
};

export const ChangeHistoryTable: React.FC<{ leadId: string }> = ({ leadId }) => {
  const [changeHistory, setChangeHistory] = useState<any[]>([]);
  const [skip, setSkip] = useState(0);
  // const [take, setTake] = useState(10);
  const [hasMore, setHasMore] = useState(true);

  const take = 8;

  const { data, loading, error } = useQuery(FETCH_LEAD_HISTORY_CHANGES, {
    variables: { lead_id: leadId, skip: skip, take: take },
    fetchPolicy: "network-only",
    onCompleted: () => {
      const newList = [...changeHistory, ...(data?.fetchLeadHistoryChanges ?? [])]
        .reduce((acc, cv) => (acc?.map((a: any) => a.id)?.includes(cv.id) ? acc : [...acc, cv]), [])
        .sort((a: any, b: any) => b.created_at - a.created_at);

      setChangeHistory(newList);

      if ((data?.fetchLeadHistoryChanges?.length || 0) < take) setHasMore(false);
      else setHasMore(true);
    },
    onError({ message, name }) {
      console.log(`Error in ${name}: `, message);
    },
  });

  const handleShowMore = () => {
    setSkip(skip + take);
  };

  if (!loading && changeHistory.length === 0 && data?.fetchLeadHistoryChanges?.length === 0)
    return (
      <ListContainer style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <AppText>No lead change history to display.</AppText>
      </ListContainer>
    );
  if (error)
    return (
      <ListContainer style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <AppErrorText>{error?.message}</AppErrorText>
      </ListContainer>
    );

  return (
    <ListContainer id="change-history-list">
      <InfiniteScroll
        dataLength={changeHistory.length}
        next={handleShowMore}
        hasMore={hasMore}
        loader={<Loading />}
        scrollableTarget="change-history-list"
        style={{ overflow: "inherit" }}
      >
        <AppTable
          sticky
          dataFor="lead-change-history-table"
          columns={["Date/Time", "Field", "Old Value", "New Value", "Who Initiated?"]}
        >
          {!!changeHistory.length &&
            changeHistory.map((change: any, index: number) => (
              <TableRow key={`change-history-${index}-${change.id}`}>
                <TableTD>{moment.unix(change.created_at / 1000).format("MM/DD/YYYY hh:mm A")}</TableTD>
                <TableTD
                  data-for="lead-change-history-table"
                  data-tip={
                    !!SYSTEM_FIELD_NAME_TO_LABEL[change.field.toUpperCase() as keyof typeof SYSTEM_FIELD_NAME_TO_LABEL]
                      ? "Lead field"
                      : "Custom field"
                  }
                >
                  {SYSTEM_FIELD_NAME_TO_LABEL[change.field.toUpperCase() as keyof typeof SYSTEM_FIELD_NAME_TO_LABEL] ||
                    `${change.field}`}
                </TableTD>
                <TableTD>{change.old_value}</TableTD>
                <TableTD>{change.new_value}</TableTD>
                <TableTD data-for="lead-change-history-table" data-tip={change?.custom_updated_by}>
                  {change.user?.full_name || change?.custom_updated_by}
                </TableTD>
              </TableRow>
            ))}
        </AppTable>
      </InfiniteScroll>
    </ListContainer>
  );
};

const ListContainer = styled.div`
  width: 100%;
  height: 299px;
  overflow: auto;
`;
